'use client';

import { HomepagePostCardsQueryResult } from '@app/sanity.types';
import { TheBoatDBConfigType, useConfig } from '@marine/configs';
import { Tag, Typography } from 'antd';
import Link from 'next/link';
import { FC } from 'react';
import { SanityImage } from 'sanity-image';
import styles from './BlogCard.module.scss';

interface Props {
  post: HomepagePostCardsQueryResult[number];
}

const BlogCard: FC<Props> = ({ post }) => {
  const { projectId, dataset } = useConfig<TheBoatDBConfigType>();

  // The blog is served by this app at /blog. Building the href from the configured
  // `theboatDbBlogUrl` pointed every homepage blog card at blog.theboatdb.com, which 301s here —
  // ten internal redirects off the homepage alone, and the whole of Seobility's internal-redirect
  // list (MDC-4221). Same-origin means no redirect hop, and no reason to open a new tab.
  return (
    <Link href={`/blog/${post.slug.current ?? ''}`} className={styles.link}>
      <div className={styles.wrapper}>
        <div className={styles.cover}>
          {post.coverImage?.asset?._ref && (
            <SanityImage
              id={post.coverImage.asset?._ref}
              projectId={projectId}
              dataset={dataset}
              height={317}
              width={564}
              alt={post.coverImage.alt || post.title}
            />
          )}
        </div>
        <div className={styles.dataWrapper}>
          {Array.isArray(post.categories) && post.categories.length > 0 && (
            <div className={styles.categories}>
              {post.categories.map((category) => (
                <Tag key={category._id} className={styles.tag}>
                  {category.title}
                </Tag>
              ))}
            </div>
          )}
          <h3 className={styles.title}>{post.title}</h3>
          {post.excerpt && (
            <Typography.Paragraph ellipsis={{ rows: 3 }} className={styles.excerpt}>
              {post.excerpt}
            </Typography.Paragraph>
          )}
        </div>
      </div>
    </Link>
  );
};

export { BlogCard };
